import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchSectionById,
  fetchRoles,
  createSection,
  updateSectionAction,
  createSubsection,
  updateSubsectionAction,
  deleteSubsection,
  setSelectedSection,
  setValidationErrors,
  setSubsectionDialogOpen,
  setSelectedSubsection,
  setEditingSubsection,
  setSubsectionValidationErrors,
} from '../../store/reducers/sectionsSlice';
import { CreateSectionRequest, UpdateSectionRequest, CreateSubsectionRequest, UpdateSubsectionRequest } from '../../services/sectionsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2),
    },
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  subsectionSection: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  subsectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.25),
    },
  },
}));

const SectionsEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditing = Boolean(id);
  const sectionId = id ? parseInt(id, 10) : null;

  const {
    selectedSection,
    roles,
    loading,
    error,
    validationErrors,
    isSubsectionDialogOpen,
    selectedSubsection,
    isEditingSubsection,
    subsectionValidationErrors,
  } = useSelector((state: RootState) => state.sections);

  // Form state
  const [formData, setFormData] = useState({
    Name: '',
    Roles: [] as number[],
    Active: true,
  });

  // Subsection form state
  const [subsectionFormData, setSubsectionFormData] = useState({
    Name: '',
    URL: '',
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchRoles());
    
    if (isEditing && sectionId) {
      dispatch(fetchSectionById(sectionId));
    } else {
      dispatch(setSelectedSection(null));
    }

    // Clear validation errors
    dispatch(setValidationErrors([]));
  }, [dispatch, isEditing, sectionId]);

  // Update form data when selectedSection changes
  useEffect(() => {
    if (isEditing && selectedSection) {
      setFormData({
        Name: selectedSection.Name || '',
        Roles: selectedSection.Roles || [],
        Active: selectedSection.Active ?? true,
      });
    } else if (!isEditing) {
      setFormData({
        Name: '',
        Roles: [],
        Active: true,
      });
    }
  }, [selectedSection, isEditing]);

  // Update subsection form when selectedSubsection changes
  useEffect(() => {
    if (selectedSubsection) {
      setSubsectionFormData({
        Name: selectedSubsection.Name || '',
        URL: selectedSubsection.URL || '',
      });
    } else {
      setSubsectionFormData({
        Name: '',
        URL: '',
      });
    }
  }, [selectedSubsection]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(setValidationErrors([]));
    }
  };

  const handleSubsectionInputChange = (field: string, value: any) => {
    setSubsectionFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (subsectionValidationErrors.length > 0) {
      dispatch(setSubsectionValidationErrors([]));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isEditing && sectionId) {
      const updateData: UpdateSectionRequest = {
        Id: sectionId,
        ...formData,
      };
      dispatch(updateSectionAction(updateData));
    } else {
      const createData: CreateSectionRequest = {
        ...formData,
      };
      dispatch(createSection(createData));
    }
  };

  const handleCancel = () => {
    navigate('/sections');
  };

  const handleBack = () => {
    navigate('/sections');
  };

  // Subsection handlers
  const handleAddSubsection = () => {
    dispatch(setSelectedSubsection(null));
    dispatch(setEditingSubsection(false));
    dispatch(setSubsectionDialogOpen(true));
  };

  const handleEditSubsection = (subsection: any) => {
    dispatch(setSelectedSubsection(subsection));
    dispatch(setEditingSubsection(true));
    dispatch(setSubsectionDialogOpen(true));
  };

  const handleDeleteSubsection = (subsectionId: number) => {
    if (window.confirm('Are you sure you want to delete this subsection?')) {
      dispatch(deleteSubsection(subsectionId));
    }
  };

  const handleSubsectionSubmit = () => {
    if (!sectionId) return;

    if (isEditingSubsection && selectedSubsection) {
      const updateData: UpdateSubsectionRequest = {
        Id: selectedSubsection.Id,
        SectionId: sectionId,
        ...subsectionFormData,
      };
      dispatch(updateSubsectionAction(updateData));
    } else {
      const createData: CreateSubsectionRequest = {
        SectionId: sectionId,
        ...subsectionFormData,
      };
      dispatch(createSubsection(createData));
    }
  };

  const handleCloseSubsectionDialog = () => {
    dispatch(setSubsectionDialogOpen(false));
  };

  if (loading && isEditing && !selectedSection) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper className={classes.paper}>
        {/* Header */}
        <Box className={classes.header}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          >
            Back to Sections
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Section' : 'Add New Section'}
          </Typography>
          {isEditing && selectedSection && (
            <Typography variant="subtitle1" color="textSecondary">
              Section: {selectedSection.Name}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginBottom: 24 }} />

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                variant="outlined"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                placeholder="Enter section name"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Roles with Access</InputLabel>
                <Select
                  multiple
                  value={formData.Roles}
                  onChange={(e) => handleInputChange('Roles', e.target.value)}
                  label="Roles with Access"
                  renderValue={(selected) => (
                    <Box className={classes.chipContainer}>
                      {(selected as number[]).map((roleId) => {
                        const role = roles.find(r => r.Id === roleId);
                        return (
                          <Chip
                            key={roleId}
                            label={role?.Name || `Role ${roleId}`}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.Id} value={role.Id}>
                      {role.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active}
                    onChange={(e) => handleInputChange('Active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          {/* Button Group */}
          <Box className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Section' : 'Create Section'
              )}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>

        {/* Subsections Section */}
        {isEditing && selectedSection && (
          <Box className={classes.subsectionSection}>
            <Box className={classes.subsectionHeader}>
              <Typography variant="h6">Subsections</Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddSubsection}
              >
                Add New Subsection
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSection.Subsections && selectedSection.Subsections.length > 0 ? (
                    selectedSection.Subsections.map((subsection) => (
                      <TableRow key={subsection.Id}>
                        <TableCell>{subsection.Name}</TableCell>
                        <TableCell>{subsection.URL}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditSubsection(subsection)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSubsection(subsection.Id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No subsections found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Subsection Dialog */}
      <Dialog 
        open={isSubsectionDialogOpen} 
        onClose={handleCloseSubsectionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditingSubsection ? 'Edit Subsection' : 'Add New Subsection'}
        </DialogTitle>
        <DialogContent>
          {subsectionValidationErrors.length > 0 && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {subsectionValidationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
          
          <TextField
            fullWidth
            required
            label="Name"
            variant="outlined"
            value={subsectionFormData.Name}
            onChange={(e) => handleSubsectionInputChange('Name', e.target.value)}
            placeholder="Enter subsection name"
            style={{ marginBottom: 16 }}
          />
          
          <TextField
            fullWidth
            required
            label="URL"
            variant="outlined"
            value={subsectionFormData.URL}
            onChange={(e) => handleSubsectionInputChange('URL', e.target.value)}
            placeholder="Enter subsection URL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubsectionDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubsectionSubmit}
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {isEditingSubsection ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SectionsEdit; 